import { gql } from "@apollo/client";

import {
  LOGISTICS_DHL_ACCOUNT_SETTINGS_FIELDS,
  LOGISTICS_DHL_SHIPPING_SETTINGS_FIELDS,
  LOGISTICS_HEXNODE_DEVICE,
  LOGISTICS_SHIPPING_LABEL_FIELDS,
  LOGISTICS_WORKSPACE_ONE_DEVICE,
  LOGISTICS_WORKSPACE_ONE_MATCH,
  SALESFORCE_REPORT_SECTION,
} from "./fragments";

export const ORGANIZATION_CONTRACTS = gql`
  query OrganizationContracts($after: String, $organizationId: UUID) {
    organizationContracts(after: $after, organizationId: $organizationId) {
      availability
      organizationName
      items {
        id
        name
        startDate
        endDate
        status
        product
        productName
        flavour
        plan
        sponsoringOrganization
      }
      nextCursor
    }
  }
`;

export const ORGANIZATION_BILLING = gql`
  query OrganizationBilling($after: String, $organizationId: UUID) {
    organizationBilling(after: $after, organizationId: $organizationId) {
      availability
      organizationName
      invoices {
        id
        number
        documentDate
        totalGross
        currency
      }
      nextCursor
    }
  }
`;

export const CUSTOMER_SERVICE_ORGANIZATION_BILLING_ACCESS = gql`
  query CustomerServiceOrganizationBillingAccess($organizationIds: [UUID!]!) {
    customerServiceOrganizationBillingAccess(organizationIds: $organizationIds) {
      organizationId
      accessEnabled
      mappingConfigured
      invoiceAccountConfigured
      salesforceAccountId
      salesforceAccountName
      frisbiiCustomerId
      mappingVersion
    }
  }
`;

export const CUSTOMER_SERVICE_BILLING_SALESFORCE_ACCOUNTS = gql`
  query CustomerServiceBillingSalesforceAccounts($search: String!) {
    customerServiceBillingSalesforceAccounts(search: $search) {
      id
      name
    }
  }
`;

export const CUSTOMER_SERVICE_BILLING_CUSTOMER = gql`
  query CustomerServiceBillingCustomer($salesforceAccountId: String!, $frisbiiCustomerId: String) {
    customerServiceBillingCustomer(salesforceAccountId: $salesforceAccountId, frisbiiCustomerId: $frisbiiCustomerId) {
      id
      externalCustomerId
      displayName
      debtorAccount
    }
  }
`;

export const CUSTOMER_SERVICE_SALESFORCE_DASHBOARD = gql`
  query CustomerServiceSalesforceDashboard {
    customerServiceSalesforceDashboard {
      lastSyncedAt
      contractCount
      subscriptionCount
      unclassifiedSubscriptions
      activeB2BProducts {
        label
        count
      }
      activeB2CProducts {
        label
        count
      }
      activeLicenceProducts {
        label
        count
      }
      contractSegments {
        recordType
        eligibleContracts
        missingMrrContracts
        missingSubscriptionCountContracts
        totalMrr
        averageMrr
        averageActiveSubscriptionItems
      }
      currency
      history {
        createdDaily {
          periodStart
          segment
          category
          count
        }
        createdWeekly {
          periodStart
          segment
          category
          count
        }
        createdMonthly {
          periodStart
          segment
          category
          count
        }
        cancelledMonthly {
          periodStart
          segment
          category
          count
        }
        endedMonthly {
          periodStart
          segment
          category
          count
        }
        missingCreatedDates
        missingCancellationDates
        missingEndDates
      }
    }
  }
`;

export const SALESFORCE_REPORT_DASHBOARD = gql`
  ${SALESFORCE_REPORT_SECTION}
  query SalesforceReportDashboard($dashboard: SalesforceReportDashboard!) {
    salesforceReportDashboard(dashboard: $dashboard) {
      dashboard
      salesforceId
      lastSyncedAt
      components {
        key
        reportId
        layout {
          column
          row
          width
          height
        }
        chart
        unavailable
        sortByValue
        showPercentages
        source
        groupings {
          field
          granularity
        }
        measures {
          aggregate
          field
        }
        points {
          group
          series
          values
        }
        unreadableFields
        missingFields
        pendingObjects
        blockKey
        showValues
        sortLegendValues
        table {
          ...SalesforceReportSection
        }
      }
    }
  }
`;

export const SALESFORCE_REPORT = gql`
  ${SALESFORCE_REPORT_SECTION}
  query SalesforceReport(
    $reportId: String!
    $component: SalesforceReportComponentKey
    $values: [String]
    $columnFilters: [SalesforceReportColumnFilterInput!]
  ) {
    salesforceReport(reportId: $reportId, component: $component, values: $values, columnFilters: $columnFilters) {
      reportId
      type
      format
      source
      dateRange {
        field
        relative
        lastDays
        from
        to
      }
      filters {
        field
        operator
        values
      }
      filterLogic
      linkedFilters {
        field
        operator
        values
      }
      down {
        field
        granularity
        descending
        link {
          kind
          object
        }
      }
      across {
        field
        granularity
        descending
        link {
          kind
          object
        }
      }
      summaries {
        aggregate
        field
        formula
      }
      columns {
        field
        type
        link {
          kind
          object
        }
      }
      showSubtotals
      showGrandTotal
      groups {
        down {
          value
          label
        }
        across {
          value
          label
        }
        count
        values
      }
      rows {
        down {
          value
          label
        }
        values
        links
      }
      hasMoreRows
      rowLimit
      chart
      chartGroupings {
        field
        granularity
      }
      chartMeasures {
        aggregate
        field
      }
      chartPoints {
        group
        series
        values
      }
      lastSyncedAt
      unreadableFields
      blockKey
      unavailable
      missingFields
      pendingObjects
      chartUnavailable
      chartMissingFields
      chartPendingObjects
      showValues
      sortLegendValues
      selectedBlock
      blocks {
        ...SalesforceReportSection
      }
    }
  }
`;

export const CUSTOMER_SERVICE_SALESFORCE_RECORDS = gql`
  query CustomerServiceSalesforceRecords($input: SalesforceRecordsInput!) {
    customerServiceSalesforceRecords(input: $input) {
      totalCount
      records {
        salesforceId
        name
        recordType
        status
        productName
        productFamily
        segment
        sponsoringKrankenkasse
        createdAt
        startDate
        endDate
        cancellationDate
        cancellationType
        contractSalesforceId
        contractStatus
        contractNumber
        contractName
        contractRecordType
        trialPeriodDays
        billingStartDate
        price
        assetType
        changedFromSalesforceId
        changedFromName
        changedIntoSalesforceId
        changedIntoName
        inTrial
        lostReason
        billingStarted
        productSalesforceId
      }
    }
  }
`;

export const CUSTOMER_SERVICE_SALESFORCE_CONTRACTS = gql`
  query CustomerServiceSalesforceContracts($segment: SalesforceSegment!) {
    customerServiceSalesforceContracts(segment: $segment) {
      totalCount
      records {
        salesforceId
        status
        recordType
        startDate
        endDate
        mrr
        activeSubscriptionItems
      }
    }
  }
`;

export const LEARNING_VIEWER_QUERY = gql`
  query LearningViewer {
    learningViewer {
      id
      firstName
      lastName
      organizationId
    }
  }
`;

export const CUSTOMER_SERVICE_LEARNING_ORGANIZATIONS = gql`
  query CustomerServiceOrganizations {
    customerServiceLearningOrganizations {
      id
      name
    }
  }
`;

export const CUSTOMER_SERVICE_ORGANIZATION_LEARNING = gql`
  query CustomerServiceOrganizationLearning($organizationId: UUID!) {
    customerServiceLearningCatalog(organizationId: $organizationId) {
      courses {
        id
        version
        locale
        title
        description
        thumbnailFilename
        lessonCount
        estimatedDurationMinutes
        tags
      }
      learningPaths {
        id
        version
        locale
        title
        description
        thumbnailFilename
        enforceOrder
        courses {
          courseId
          position
        }
      }
    }
    organizationLearningGrants(organizationId: $organizationId) {
      organizationId
      grants {
        id
        itemKind
        courseId
        learningPathId
        active
      }
    }
  }
`;

export const MANAGER_LEARNING_CATALOG_AND_CAREGIVERS = gql`
  query ManagerLearningCatalogAndCaregivers {
    managerLearningCatalog {
      courses {
        id
        version
        locale
        title
        description
        thumbnailFilename
        lessonCount
        estimatedDurationMinutes
        passingScorePercent
        tags
      }
      learningPaths {
        id
        version
        locale
        title
        description
        thumbnailFilename
        enforceOrder
        courses {
          courseId
          position
        }
      }
      courseAccess {
        courseId
        directAccess
        pathAccess
      }
    }
    managerLearningCaregivers {
      userId
      organizationId
      firstName
      lastName
      displayName
    }
  }
`;

export const MANAGER_LEARNING_ASSIGNMENTS = gql`
  query ManagerLearningAssignments($userIds: [UUID!]) {
    managerLearningAssignments(userIds: $userIds) {
      id
      organizationId
      userId
      itemKind
      courseId
      learningPathId
      learningPathVersion
      kind
      assignedBy
      assignedAt
      unassignedBy
      unassignedAt
      active
      enrollmentIds
    }
  }
`;

export const MANAGER_LEARNING_PROGRESS = gql`
  query ManagerLearningProgress($filter: LearningProgressFilter!, $assignmentUserIds: [UUID!]) {
    managerLearningCatalog {
      courses {
        id
        title
      }
      learningPaths {
        id
        version
        title
        courses {
          courseId
          position
        }
      }
    }
    managerLearningCaregivers {
      userId
      organizationId
      displayName
      locationNames
    }
    managerLearningAssignments(userIds: $assignmentUserIds) {
      id
      userId
      itemKind
      learningPathId
      learningPathVersion
      active
      enrollmentIds
    }
    learningProgressReport(filter: $filter) {
      entries {
        enrollmentId
        organizationId
        userId
        courseId
        status
        firstStartedAt
        completedAt
        bestScorePercent
        quizAttempts {
          id
          courseVersion
          serverScorePercent
          serverPassed
          finishedAt
        }
      }
    }
  }
`;

export const MY_LEARNING_CATALOG_QUERY = gql`
  query MyLearningCatalog {
    myLearningCatalog {
      courses {
        id
        version
        locale
        title
        description
        thumbnailFilename
        estimatedDurationMinutes
        passingScorePercent
        tags
      }
      learningPaths {
        id
        version
        locale
        title
        description
        thumbnailFilename
        enforceOrder
        courses {
          courseId
          position
        }
      }
      courseAccess {
        courseId
        currentVersion
        directAccess
        selfAccess
        pathAccess
        locked
      }
      selfEnrollCourseIds
      selfEnrollPathIds
    }
  }
`;

export const LEARNING_COURSE_PAYLOAD_QUERY = gql`
  query LearningCoursePayload($courseId: UUID!, $version: String!) {
    learningCoursePayload(courseId: $courseId, version: $version) {
      courseId
      version
      payload
    }
  }
`;

export const MY_LEARNING_QUERY = gql`
  query MyLearning {
    myLearning {
      courses {
        courseId
        currentVersion
        startedVersions
        status
        completedLessonIds
        bestScorePercent
        directAccess
      }
      pathSources {
        assignmentId
        pathId
        pathVersion
        orderedCourses {
          courseId
          available
          locked
        }
      }
    }
  }
`;

export const ORGANIZATION_LIST = gql`
  query OrganizationsQuery($first: Int, $offset: Int) {
    organizations(first: $first, offset: $offset) {
      nodes {
        id
        name
        externalId
        postalCode
        patients {
          totalCount
        }
        practitioners {
          totalCount
          nodes {
            id
            role
          }
        }
        organizationManagers {
          nodes {
            user {
              email
              name
            }
          }
        }
      }
      totalCount
    }
  }
`;

export const ORGANIZATION_GET_ONE = gql`
  query OrganizationQuery($id: UUID!) {
    organization(id: $id) {
      id
      name
      externalId
      street
      postalCode
      city
      additionalInfo
      locations {
        nodes {
          id
          name
          externalId
          type
          status
          patients {
            totalCount
          }
          locationPractitioners {
            totalCount
          }
          devices(condition: { unenrolledAt: null }) {
            totalCount
          }
        }
      }
      patients {
        totalCount
        nodes {
          user {
            id
            firstName
            lastName
            avatarUrl
          }
        }
      }
      practitioners {
        totalCount
        nodes {
          id
          user {
            id
            firstName
            lastName
            avatarUrl
          }
        }
      }
    }
  }
`;
export const LOCATION_LIST = gql`
  query LocationsQuery($first: Int, $offset: Int, $condition: LocationCondition) {
    locations(first: $first, offset: $offset, condition: $condition) {
      nodes {
        id
        name
        externalId
        sourceSystem
        type
        status
        partOfId
        mode
        organization {
          id
          name
        }
        patients {
          totalCount
        }
        locationPractitioners {
          totalCount
        }
        devices(condition: { unenrolledAt: null }) {
          totalCount
        }
      }
      totalCount
    }
  }
`;

export const LOCATION_SUMMARY_LIST = gql`
  query LocationSummaryList($first: Int, $offset: Int, $condition: LocationSummaryCondition) {
    locationSummaries(first: $first, offset: $offset, condition: $condition, orderBy: [NAME_ASC]) {
      nodes {
        id
        organizationId
        partOfId
        name
        type
        immediateChildCount
        patientCount
        practitionerCount
        deviceCount
      }
      totalCount
    }
  }
`;

export const LOCATION_OPTIONS_LIST = gql`
  query LocationOptionsList($first: Int, $offset: Int, $condition: LocationCondition) {
    locations(first: $first, offset: $offset, condition: $condition) {
      nodes {
        id
        name
        type
        partOfId
        organizationId
      }
      totalCount
    }
  }
`;

export const LOCATION_GET_ONE = gql`
  query LocationQuery($id: UUID!) {
    location(id: $id) {
      id
      name
      externalId
      sourceSystem
      type
      status
      partOfId
      mode
      organization {
        id
        name
      }
      patients {
        nodes {
          id
          patientNumber
          gender
          birthDate
          user {
            id
            name
            firstName
            lastName
            avatarUrl
          }
        }
      }
      locationPractitioners {
        nodes {
          practitioner {
            id
            status
            employeeNumber
            emailAddress
            user {
              id
              firstName
              lastName
              avatarUrl
            }
          }
        }
      }
      devices {
        nodes {
          id
          deviceId
          status
          manufacturer
          model
          osVersion
          appVersion
          contentVersion
          name
        }
      }
    }
  }
`;

export const PATIENT_LIST = gql`
  query PatientsQuery($first: Int, $offset: Int, $condition: PatientCondition) {
    patients(first: $first, offset: $offset, condition: $condition) {
      nodes {
        id
        sourceSystem
        gender
        birthDate
        patientNumber
        location {
          id
          type
        }
        user {
          id
          name
          firstName
          lastName
          avatarUrl
        }
        organization {
          id
          name
        }
        locationHierarchy {
          nodes {
            level
            locationId
            locationName
            locationType
            externalId
          }
        }
      }
      totalCount
    }
  }
`;

export const DASHBOARD_PATIENT_COUNT = gql`
  query DashboardPatientCount($targetOrganizationId: UUID, $selectedLocationId: UUID) {
    dashboardPatients(targetOrganizationId: $targetOrganizationId, selectedLocationId: $selectedLocationId, first: 0) {
      totalCount
    }
  }
`;

export const DASHBOARD_PATIENT_LIST = gql`
  query DashboardPatientList($first: Int, $offset: Int, $targetOrganizationId: UUID, $selectedLocationId: UUID) {
    dashboardPatients(
      targetOrganizationId: $targetOrganizationId
      selectedLocationId: $selectedLocationId
      first: $first
      offset: $offset
    ) {
      nodes {
        id
        sourceSystem
        gender
        birthDate
        patientNumber
        location {
          id
          type
        }
        user {
          id
          name
          firstName
          lastName
          avatarUrl
        }
        organization {
          id
          name
        }
        locationHierarchy {
          nodes {
            level
            locationId
            locationName
            locationType
            externalId
          }
        }
      }
      totalCount
    }
  }
`;

export const PATIENT_GET_ONE = gql`
  query PatientQuery($id: UUID!) {
    patient(id: $id) {
      id
      sourceSystem
      externalId
      gender
      birthDate
      patientNumber
      location {
        id
        type
      }
      user {
        id
        name
        firstName
        lastName
        email
        avatarUrl
        avatar
        salutation
      }
      organization {
        id
        name
      }
      locationHierarchy {
        nodes {
          level
          locationId
          locationName
          locationType
          externalId
        }
      }
    }
  }
`;

export const ACTIVITY_GOALS_LIST = gql`
  query ActivityGoalsList($userId: UUID!) {
    activityGoalsList(condition: { userId: $userId }) {
      id
      userId
      kind
      weeklyGoal
    }
  }
`;

export const ACTIVITY_GOAL_PROGRESS = gql`
  query ActivityGoalProgress($userId: UUID!) {
    activityGoalProgress(userId: $userId) {
      kind
      weeklyGoalMinutes
      doneMinutes
    }
  }
`;

export const ACTIVITY_RECOMMENDATIONS = gql`
  query ActivityRecommendations(
    $userId: UUID!
    $kind: ActivityGoalEnum!
    $limit: Int
    $targetDurationMinutes: Int
    $excludeContentIds: [Int!]
  ) {
    activityRecommendations(
      userId: $userId
      kind: $kind
      limit: $limit
      targetDurationMinutes: $targetDurationMinutes
      excludeContentIds: $excludeContentIds
    ) {
      cmsContentId
      cmsContentKind
      contentUuid
      title
      durationMinutes
      kind
      thumbnailFilename
      isPlanned
    }
  }
`;

export const PATIENT_PROCEDURES = gql`
  query PatientProcedures($patientId: UUID!) {
    procedureParticipants(condition: { patientId: $patientId }) {
      nodes {
        procedure {
          id
          codeDisplay
          plannedStart
          status
        }
      }
    }
  }
`;

export const PRACTITIONER_LIST = gql`
  query PractitionersQuery($first: Int, $offset: Int, $condition: PractitionerCondition) {
    practitioners(first: $first, offset: $offset, condition: $condition) {
      nodes {
        id
        sourceSystem
        status
        birthDate
        emailAddress
        employeeNumber
        gender
        user {
          id
          firstName
          lastName
          avatarUrl
          authStatus
        }
        organization {
          id
          name
        }
        practitioners {
          nodes {
            name
            id
            type
            partOfId
          }
        }
      }
      totalCount
    }
  }
`;

export const DASHBOARD_PRACTITIONER_COUNT = gql`
  query DashboardPractitionerCount($targetOrganizationId: UUID, $selectedLocationId: UUID) {
    dashboardPractitioners(
      targetOrganizationId: $targetOrganizationId
      selectedLocationId: $selectedLocationId
      first: 0
    ) {
      totalCount
    }
  }
`;

export const DASHBOARD_PRACTITIONER_LIST = gql`
  query DashboardPractitionerList($first: Int, $offset: Int, $targetOrganizationId: UUID, $selectedLocationId: UUID) {
    dashboardPractitioners(
      targetOrganizationId: $targetOrganizationId
      selectedLocationId: $selectedLocationId
      first: $first
      offset: $offset
    ) {
      nodes {
        id
        sourceSystem
        status
        birthDate
        emailAddress
        employeeNumber
        gender
        user {
          id
          firstName
          lastName
          avatarUrl
          authStatus
        }
        organization {
          id
          name
        }
        practitioners {
          nodes {
            name
            id
            type
            partOfId
          }
        }
      }
      totalCount
    }
  }
`;

export const PRACTITIONER_GET_ONE = gql`
  query PractitionerQuery($id: UUID!) {
    practitioner(id: $id) {
      id
      sourceSystem
      externalId
      birthDate
      emailAddress
      employeeNumber
      telephoneNumber
      gender
      status
      role
      motherTongue
      user {
        id
        firstName
        lastName
        avatarUrl
        avatar
        salutation
        authStatus
        authStatusValidUntil
      }
      organization {
        id
        name
      }
      practitioners {
        nodes {
          name
          id
          type
        }
      }
    }
  }
`;

export const GET_UPLOAD_LINKS_WITH_KEYS = gql`
  query GetUploadLinksWithKeys($key: String!) {
    getUploadLinksWithKeys(keys: [$key]) {
      key
      url
    }
  }
`;

export const DEVICE_LIST = gql`
  query DevicesQuery($first: Int, $offset: Int, $condition: DeviceCondition) {
    devices(first: $first, offset: $offset, condition: $condition) {
      nodes {
        id
        deviceId
        status
        unenrolledAt
        manufacturer
        model
        osVersion
        appVersion
        contentVersion
        name
        location {
          id
          name
        }
        organization {
          id
          name
        }
      }
      totalCount
    }
  }
`;

export const DEVICE_GET_ONE = gql`
  query DeviceQuery($id: UUID!) {
    device(id: $id) {
      id
      deviceId
      status
      unenrolledAt
      manufacturer
      model
      osVersion
      appVersion
      contentVersion
      name
      location {
        id
        name
      }
      organization {
        id
        name
      }
    }
  }
`;

export const DOCUMENTATION_SYNC_FIELDS = gql`
  fragment DocumentationSyncFields on DocumentationSync {
    id
    kind
    seniorId
    provider
    status
    skipReason
    attempts
    lastError
    sentAt
    outcomeAt
    updatedAt
  }
`;

export const SALESFORCE_INTEGRATION_FIELDS = gql`
  fragment SalesforceIntegrationFields on SalesforceIntegration {
    loginUrl
    clientId
    hasClientSecret
    anonymize
    anonymizationRequired
    schedule
    dailyTime
    intervalHours
    windowStart
    windowEnd
    windowIntervalMinutes
    outsideIntervalHours
    updatedAt
    nextRunAt
  }
`;

export const SALESFORCE_SYNC_RUNS = gql`
  query SalesforceSyncRuns {
    salesforceSyncRuns {
      id
      trigger
      status
      mode
      anonymized
      startedAt
      finishedAt
      phase
      phaseRecords
      progressAt
      counts {
        products
        accounts
        contracts
        items
        users
        contacts
        leads
        orders
        cases
        emails
        caseComments
        caseHistories
        calls
      }
      unavailable
      apiCalls
      apiRequestsRemaining
      apiRequestsMax
      errorCode
    }
  }
`;

export const SALESFORCE_INTEGRATION = gql`
  query SalesforceIntegration {
    salesforceIntegration {
      ...SalesforceIntegrationFields
    }
  }
  ${SALESFORCE_INTEGRATION_FIELDS}
`;

export const MARKETING_DELIVERY_SETTINGS_FIELDS = gql`
  fragment MarketingDeliverySettingsFields on MarketingDeliverySettings {
    availableProviders {
      type
      name
    }
    provider
    hasApiKey
    hasApiSecret
    hasWebhookSecret
    sendingDomain
    fromAddress
    fromName
    replyToAddress
    updatedAt
    recipientsRestricted
    allowedRecipients
  }
`;

export const MARKETING_DELIVERY_SETTINGS = gql`
  query MarketingDeliverySettings {
    marketingDeliverySettings {
      ...MarketingDeliverySettingsFields
    }
  }
  ${MARKETING_DELIVERY_SETTINGS_FIELDS}
`;

export const MARKETING_SENDING_DOMAIN = gql`
  query MarketingSendingDomain {
    marketingSendingDomain {
      domain
      checkedAt
      records {
        purpose
        type
        name
        value
        status
        found
      }
    }
  }
`;

export const PRACTITIONER_ACTIVITIES = gql`
  query PractitionerActivitiesQuery(
    $userId: UUID!
    $first: Int
    $offset: Int
    $orderBy: [ActivitySessionsOrderBy!]
    $condition: ActivitySessionCondition
  ) {
    user(id: $userId) {
      activitySessionsByCaregiverId(first: $first, offset: $offset, orderBy: $orderBy, condition: $condition) {
        nodes {
          id
          beganAt
          endedAt
          kind
          activity {
            id
            kind
            contentActivity {
              content {
                cmsContentId
                cmsContentKind
                cmsNextContentId
                cmsNextContentKind
                title
                metaTags {
                  nodes {
                    metaTag {
                      tag
                      kind
                    }
                  }
                }
              }
            }
            customActivity {
              name
            }
            featureActivity {
              kind
            }
          }
          activityParticipants {
            nodes {
              senior {
                id
                firstName
                lastName
                name
                avatarUrl
              }
            }
          }
          noteList {
            id
            note
            kind
            generationStatus
            publishedAt
            documentationSyncsList {
              ...DocumentationSyncFields
            }
            mdDocumentation
            createdAt
            updatedAt
            author {
              id
              firstName
              lastName
              name
            }
          }
        }
        totalCount
        pageInfo {
          hasNextPage
          hasPreviousPage
          endCursor
        }
      }
    }
  }
  ${DOCUMENTATION_SYNC_FIELDS}
`;

export const PATIENT_ACTIVITIES = gql`
  query PatientActivitiesQuery(
    $userId: UUID!
    $first: Int
    $offset: Int
    $orderBy: [ActivitySessionsOrderBy!]
    $condition: ActivitySessionCondition
  ) {
    user(id: $userId) {
      activitySessionsByActivityParticipantSeniorIdAndActivitySessionId(
        first: $first
        offset: $offset
        orderBy: $orderBy
        condition: $condition
      ) {
        nodes {
          id
          beganAt
          endedAt
          kind
          activity {
            id
            kind
            contentActivity {
              content {
                cmsContentId
                cmsContentKind
                cmsNextContentId
                cmsNextContentKind
                title
                metaTags {
                  nodes {
                    metaTag {
                      tag
                      kind
                    }
                  }
                }
              }
            }
            customActivity {
              name
            }
            featureActivity {
              kind
            }
          }
          noteList {
            id
            note
            kind
            generationStatus
            publishedAt
            documentationSyncsList {
              ...DocumentationSyncFields
            }
            mdDocumentation
            createdAt
            updatedAt
            author {
              id
              firstName
              lastName
              name
            }
          }
        }
        totalCount
        pageInfo {
          hasNextPage
          hasPreviousPage
          endCursor
        }
      }
    }
  }
  ${DOCUMENTATION_SYNC_FIELDS}
`;

export const PRACTITIONER_NOTES = gql`
  query PractitionerNotesQuery($userId: UUID!, $first: Int, $offset: Int) {
    notes(
      first: $first
      offset: $offset
      orderBy: UPDATED_AT_DESC
      condition: { authorId: $userId, excludeEmptyNote: true }
    ) {
      nodes {
        id
        note
        createdAt
        updatedAt
        author {
          id
          firstName
          lastName
          name
        }
        activitySessionList {
          activity {
            contentActivity {
              content {
                metaTags {
                  nodes {
                    metaTag {
                      tag
                      kind
                    }
                  }
                }
              }
            }
            customActivity {
              name
            }
            featureActivity {
              kind
            }
          }
        }
      }
      totalCount
      pageInfo {
        hasNextPage
        hasPreviousPage
        endCursor
      }
    }
  }
`;

export const MANAGER_INVITATIONS = gql`
  query ManagerInvitations {
    managerInvitations {
      id
      email
      status
      createdAt
      salesforceAccountId
      salesforceAccountName
    }
  }
`;

export const DEVICE_ENROLLMENT_TOKEN_FIELDS = gql`
  fragment DeviceEnrollmentTokenFields on DeviceEnrollmentToken {
    id
    organizationId
    scopeType
    scopeLocation {
      id
      name
      type
    }
    expiresAt
    maxUses
    useCount
    status
    implicit
    createdBy {
      id
      name
    }
    createdAt
    revokedAt
    redemptions {
      id
      redeemedAt
      targetLocation {
        id
        name
        type
      }
      device {
        id
        deviceId
        name
        model
        manufacturer
        status
      }
    }
    emailDeliveries {
      id
      recipientEmail
      queuedAt
      sentAt
      failedAt
      status
      sentBy {
        id
        name
      }
    }
  }
`;

export const DEVICE_ENROLLMENT_TOKENS = gql`
  query DeviceEnrollmentTokens($organizationId: UUID!) {
    deviceEnrollmentTokens(organizationId: $organizationId) {
      ...DeviceEnrollmentTokenFields
    }
  }
  ${DEVICE_ENROLLMENT_TOKEN_FIELDS}
`;

export const CUSTOMER_SERVICE_ORGANIZATION_ACCOUNTS = gql`
  query CustomerServiceOrganizationAccounts {
    customerServiceOrganizationAccounts {
      organizationId
      organizationName
      salesforceAccountId
      salesforceAccountName
    }
  }
`;

export const CUSTOMER_SERVICE_ACCOUNT_MANAGERS = gql`
  query CustomerServiceAccountManagers($salesforceAccountId: String!) {
    customerServiceAccountManagers(salesforceAccountId: $salesforceAccountId) {
      salesforceAccountId
      eligible
      ambulant
      organization {
        id
        name
      }
      contacts {
        contactId
        name
        email
        invitationState
      }
      invitations {
        id
        email
        status
        createdAt
      }
      managers {
        userId
        name
        email
        registeredAt
        lastSignInAt
      }
    }
  }
`;

export const MANAGER_REGISTRATION_CANDIDATE = gql`
  query ManagerRegistrationCandidate($email: String!, $code: String!) {
    managerRegistrationCandidate(email: $email, code: $code) {
      managerName
      organizationName
    }
  }
`;

export const STAFF_REGISTRATION_CANDIDATE = gql`
  query StaffRegistrationCandidate($email: String!, $code: String!) {
    staffRegistrationCandidate(email: $email, code: $code) {
      roles
    }
  }
`;

export const STAFF_MEMBERS = gql`
  query StaffMembers {
    staffMembers {
      id
      name
      email
      roles
    }
  }
`;

export const STAFF_INVITATIONS = gql`
  query StaffInvitations {
    staffInvitations {
      id
      email
      roles
      status
      createdAt
      invitedByName
    }
  }
`;

export const ANALYTICS_DAILY_QUERY = gql`
  query AnalyticsDailyQuery {
    dailiesList(orderBy: DATE_DESC) {
      date
      appId
      dau
      mau
      messagesSent
      picturesSent
      notesCreated
      b2BSeniorsCreated
    }
  }
`;

export const ANALYTICS_DISTRIBUTION_QUERY = gql`
  query AnalyticsDistributionQuery {
    distributionsList(orderBy: DATE_DESC) {
      date
      appId
      dimension
      label
      count
    }
  }
`;

export const ANALYTICS_CONTENT_USAGE_DAILY_QUERY = gql`
  query AnalyticsContentUsageDailyQuery {
    contentUsageDailiesList(first: 20000, orderBy: DATE_DESC) {
      date
      appId
      contentId
      cmsContentKind
      contentSubtype
      title
      completedSessionCount
      unfinishedSessionCount
      completedDurationSecondsSum
      completedDurationSessionCount
    }
  }
`;

export const MOST_USED_YOUTUBE_CONTENT = gql`
  query MostUsedYoutubeContent($dateFrom: Date!, $dateTo: Date!) {
    mostUsedYoutubeContent(dateFrom: $dateFrom, dateTo: $dateTo) {
      contentId
      title
      completedSessionCount
      unfinishedSessionCount
      averageCompletedDurationSeconds
    }
  }
`;

export const LEAST_USED_CONTENT_REPORT = gql`
  query LeastUsedContentReport($contentKind: String, $first: Int!, $offset: Int!) {
    leastUsedContentReport(contentKind: $contentKind, first: $first, offset: $offset) {
      periodStart
      periodEnd
      contentTypes
      totalCount
      items {
        contentId
        title
        contentKind
        contentSubtype
        sessionCount
        lastUsedAt
      }
    }
  }
`;

export const ME_QUERY = gql`
  query MeQuery {
    me {
      id
      kind
      firstName
      lastName
      name
      email
      organizationManager {
        organizationId
        organization {
          name
        }
      }
    }
    hasLearningContent
  }
`;

// What the portal shows depends on these fields (auth/access.ts).
export const PORTAL_ACCESS_QUERY = gql`
  query PortalAccess {
    me {
      id
      kind
    }
    hasLearningContent
    currentUserPermissions
  }
`;

export const GET_INTEGRATION_SETTINGS = gql`
  query GetIntegrationSettings($organizationId: UUID!) {
    getIntegrationSettings(organizationId: $organizationId) {
      organizationId
      hasClientSecret
      provider
      credentials {
        serverUrl
        clientId
        clientSecret
      }
    }
  }
`;

export const PLACETEL_INTEGRATION = gql`
  query PlacetelIntegration {
    placetelIntegration {
      enabled
      organizationId
      clientId
      hasClientSecret
      hasRefreshToken
      hasWebhookSecret
      phoneClientId
      hasPhoneClientSecret
      updatedAt
    }
  }
`;

export const PLACETEL_HOTLINE_NUMBERS = gql`
  query PlacetelHotlineNumbers {
    placetelHotlineNumbers
  }
`;

export const PLACETEL_OPENING_HOURS = gql`
  query PlacetelOpeningHours {
    placetelOpeningHours {
      weekday
      opens
      closes
    }
  }
`;

export const PLACETEL_PHONE_ACCOUNT_FIELDS = gql`
  fragment PlacetelPhoneAccountFields on PlacetelPhoneAccount {
    available
    connected
    email
    connectedAt
  }
`;

export const PLACETEL_PHONE_ACCOUNT = gql`
  query PlacetelPhoneAccount {
    placetelPhoneAccount {
      ...PlacetelPhoneAccountFields
    }
  }
  ${PLACETEL_PHONE_ACCOUNT_FIELDS}
`;

export const GET_INTEGRATION_EXECUTIONS = gql`
  query GetIntegrationExecutions($organizationId: UUID!, $input: IntegrationExecutionsInput) {
    integrationExecutions(organizationId: $organizationId, input: $input) {
      organizationId
      id
      userId
      provider
      status
      result
      jobId
      summary
      errorMessage
      startedAt
      completedAt
      duration
    }
  }
`;

export const GET_INTEGRATION_EXECUTION = gql`
  query GetIntegrationExecution($organizationId: UUID!, $id: UUID!) {
    integrationExecution(organizationId: $organizationId, id: $id) {
      id
      organizationId
      kind
      status
    }
  }
`;

export const GET_INTEGRATION_IMPORT_PREVIEW = gql`
  query GetIntegrationImportPreview($organizationId: UUID!) {
    integrationImportPreview(organizationId: $organizationId) {
      executionId
      organizationId
      provider
      createdAt
      expiresAt
      procedureCount
      locations {
        externalId
        type
        name
        change
        parent {
          type
          externalId
        }
      }
      patients {
        externalId
        number
        displayName
        change
        reason
        locations {
          type
          externalId
        }
      }
      practitioners {
        externalId
        number
        displayName
        change
        reason
        locations {
          type
          externalId
        }
      }
      warnings {
        entity
        reference
        reason
      }
      failures {
        entity
        reference
        reason
      }
    }
  }
`;

export const PROCEDURE_LIST = gql`
  query ProceduresQuery($first: Int, $offset: Int, $condition: ProcedureCondition, $orderBy: [ProceduresOrderBy!]) {
    procedures(first: $first, offset: $offset, condition: $condition, orderBy: $orderBy) {
      nodes {
        id
        status
        sourceSystem
        externalId
        codeSystem
        codeValue
        codeDisplay
        description
        plannedStart
        plannedEnd
        performedStart
        performedEnd
        notes
        documentation
        createdAt
        updatedAt
        acknowledgedAt
        documentationSyncsList {
          ...DocumentationSyncFields
        }
        procedureNotesList {
          procedureNotes {
            id
            note
            kind
            createdAt
            publishedAt
            documentationSyncsList {
              ...DocumentationSyncFields
            }
            seniorList {
              id
              name
            }
          }
        }
        procedureParticipants {
          nodes {
            patient {
              id
              patientNumber
              location {
                id
                partOfId
              }
              user {
                firstName
                lastName
                name
              }
            }
          }
        }
        performer {
          id
          user {
            firstName
            lastName
            name
          }
        }
        organization {
          id
          name
        }
        activitySessions {
          nodes {
            id
            beganAt
            endedAt
            activity {
              id
              kind
              contentActivity {
                content {
                  title
                }
              }
              featureActivity {
                kind
              }
              customActivity {
                name
              }
            }
          }
        }
      }
      totalCount
    }
  }
  ${DOCUMENTATION_SYNC_FIELDS}
`;

export const PROCEDURE_GET_ONE = gql`
  query ProcedureQuery($id: UUID!) {
    procedure(id: $id) {
      id
      status
      sourceSystem
      externalId
      codeSystem
      codeValue
      codeDisplay
      description
      plannedStart
      plannedEnd
      performedStart
      performedEnd
      acknowledgedAt
      documentationSyncsList {
        ...DocumentationSyncFields
      }
      notes
      documentation
      sourceData
      createdAt
      updatedAt
      procedureNotesList {
        procedureNotes {
          id
          note
          kind
          createdAt
          publishedAt
          documentationSyncsList {
            ...DocumentationSyncFields
          }
          seniorList {
            id
            name
          }
          author {
            id
            firstName
            lastName
            name
          }
        }
      }
      procedureParticipants {
        nodes {
          patient {
            id
            patientNumber
            location {
              id
              partOfId
            }
            user {
              firstName
              lastName
              name
              avatarUrl
            }
          }
        }
      }
      performer {
        id
        user {
          firstName
          lastName
          name
        }
      }
      organization {
        id
        name
      }
      activitySessions {
        nodes {
          id
          beganAt
          endedAt
          sessionStatus
          caregiver {
            id
            name
            firstName
            lastName
          }
          activity {
            id
            kind
            contentActivity {
              content {
                id
                title
                cmsContentId
                cmsContentKind
                cmsNextContentKind
                thumbnailFilename
              }
            }
            featureActivity {
              kind
            }
            customActivity {
              name
            }
          }
          activityParticipants {
            nodes {
              senior {
                id
                name
                firstName
                lastName
              }
            }
          }
        }
      }
    }
  }
  ${DOCUMENTATION_SYNC_FIELDS}
`;

export const REVEAL_DEVICE_ENROLLMENT_TOKEN = gql`
  query RevealDeviceEnrollmentToken($id: UUID!) {
    deviceEnrollmentToken(id: $id) {
      id
      code
      qrPayload
    }
  }
`;

export const CRM_VALUE_FIELDS = gql`
  fragment CrmValueFields on CrmValue {
    key
    type
    value
    referenceObject
    referenceTitle
  }
`;

export const CRM_ROW_FIELDS = gql`
  ${CRM_VALUE_FIELDS}
  fragment CrmRowFields on CrmRow {
    salesforceId
    title
    deletedInSalesforceAt
    values {
      ...CrmValueFields
    }
  }
`;

export const CUSTOMER_SERVICE_CRM_LIST = gql`
  ${CRM_ROW_FIELDS}
  query CustomerServiceCrmList($input: CrmListInput!) {
    customerServiceCrmList(input: $input) {
      object
      listView
      listViews
      totalCount
      columns {
        key
        type
      }
      rows {
        ...CrmRowFields
      }
    }
  }
`;

export const CRM_INVENTORY_UNIT_FIELDS = gql`
  fragment CrmInventoryUnitFields on CrmInventoryUnit {
    id
    name
    type
    status
    imei
    serialNumber
    hardwareSerialNumber
    simIccid
    deletedInSalesforceAt
  }
`;

export const CUSTOMER_SERVICE_CRM_RECORD = gql`
  ${CRM_ROW_FIELDS}
  ${CRM_INVENTORY_UNIT_FIELDS}
  query CustomerServiceCrmRecord($object: CrmObject!, $salesforceId: String!) {
    customerServiceCrmRecord(object: $object, salesforceId: $salesforceId, includeOpportunities: true) {
      contractItemInventoryHistory {
        id
        inventory {
          ...CrmInventoryUnitFields
        }
        atClientDate
        removedFromClientDate
        inStockDate
        deletedInSalesforceAt
      }
      contractItemOrders {
        id
        orderSalesforceId
        orderNumber
        recordType
        status
        exchangeStatus
        orderRejected
        createdBy
        createdAt
        deletedInSalesforceAt
      }
      contractItemAsset {
        salesforceId
        name
        deletedInSalesforceAt
        tablet {
          ...CrmInventoryUnitFields
        }
        sim {
          ...CrmInventoryUnitFields
        }
        hardware {
          ...CrmInventoryUnitFields
        }
      }
      object
      salesforceId
      title
      salesforceUrl
      syncedAt
      deletedInSalesforceAt
      mergedInto {
        salesforceId
        title
      }
      hasTimeline
      highlights {
        ...CrmValueFields
      }
      path {
        stages
        current
      }
      sections {
        key
        values {
          ...CrmValueFields
        }
      }
      related {
        key
        object
        totalCount
        columns {
          key
          type
        }
        rows {
          ...CrmRowFields
        }
      }
      appOrganizations {
        id
        name
      }
    }
  }
`;

export const CUSTOMER_SERVICE_CRM_TIMELINE = gql`
  query CustomerServiceCrmTimeline(
    $object: CrmObject!
    $salesforceId: String!
    $kinds: [CrmTimelineKind!]
    $after: CrmTimelineCursorInput
    $limit: Int
  ) {
    customerServiceCrmTimeline(
      object: $object
      salesforceId: $salesforceId
      kinds: $kinds
      after: $after
      limit: $limit
    ) {
      kind
      salesforceId
      at
      title
      body
      bodyNotImported
      inbound
      actor
      counterpart
      durationSeconds
      recorded
      field
      oldValue
      newValue
      caseId
      caseTitle
      provider
    }
  }
`;

export const CUSTOMER_SERVICE_CRM_SEARCH = gql`
  query CustomerServiceCrmSearch($term: String!) {
    customerServiceCrmSearch(term: $term, includeOpportunities: true) {
      object
      salesforceId
      title
      subtitle
      deletedInSalesforceAt
    }
  }
`;

export const CUSTOMER_SERVICE_CRM_PHONE_MATCHES = gql`
  query CustomerServiceCrmPhoneMatches($number: String!) {
    customerServiceCrmPhoneMatches(number: $number) {
      object
      salesforceId
      title
      subtitle
    }
  }
`;

export const CUSTOMER_SERVICE_CALL_KPIS = gql`
  query CustomerServiceCallKpis($range: CallKpiRange!) {
    customerServiceCallKpis(range: $range) {
      hotlineConfigured
      inbound
      answered
      missed
      abandoned
      notRouted
      reachability
      averageWaitSeconds
      maxWaitSeconds
      averageHandleSeconds
      outbound
      agents {
        agentKey
        name
        answered
        outbound
        averageHandleSeconds
      }
      syncedAt
      syncError
    }
  }
`;

export const CUSTOMER_SERVICE_PROVIDER_CALLS = gql`
  query CustomerServiceProviderCalls(
    $range: CallKpiRange!
    $filter: CallListFilter!
    $agentKey: String
    $first: Int
    $after: String
  ) {
    customerServiceProviderCalls(range: $range, filter: $filter, agentKey: $agentKey, first: $first, after: $after) {
      totalCount
      rows {
        id
        startedAt
        direction
        outcome
        customerNumber
        agentName
        waitSeconds
        handleSeconds
        withinOpeningHours
      }
      endCursor
      hasMore
    }
  }
`;

export const BILLWERK_PLAN_VARIANT_FIELDS = gql`
  fragment BillwerkPlanVariantFields on BillwerkPlanVariant {
    state
    planVariantId
    name
    hidden
    url
    billwerkPlanId
    priceBookId
    priceBookName
  }
`;

const FINANCE_CONTRACT_ITEM_FIELDS = gql`
  ${BILLWERK_PLAN_VARIANT_FIELDS}
  fragment FinanceContractItemFields on FinanceContractItem {
    salesforceId
    salesforceUrl
    name
    status
    contractNumber
    accountName
    accountRecordType
    productName
    price
    startDate
    endDate
    billingStartDate
    billingTool
    billingStarted
    billingStopped
    cancellationType
    cancellationDate
    lostReason
    planVariant {
      ...BillwerkPlanVariantFields
    }
  }
`;

export const FINANCE_NOTIFICATIONS = gql`
  ${FINANCE_CONTRACT_ITEM_FIELDS}
  query FinanceNotifications($rule: FinanceNotificationRule, $after: String) {
    financeNotifications(rule: $rule, after: $after) {
      nodes {
        id
        rule
        openedAt
        item {
          ...FinanceContractItemFields
        }
        billwerkEffectiveFrom
        returnOrder {
          salesforceId
          salesforceUrl
          orderNumber
          status
          completedDate
        }
        noCompletedReturn
        changedInto {
          ...FinanceContractItemFields
        }
        handledStatus
        handledEndDate
        handledAt
      }
      totalCount
      hasNextPage
      endCursor
      sync {
        lastSucceededAt
        lastRunFailed
        unreadableFields
      }
    }
  }
`;

export const FINANCE_NOTIFICATION_COUNT = gql`
  query FinanceNotificationCount {
    financeNotificationCount
  }
`;

const BILLWERK_MATCH_ACCOUNT_FIELDS = gql`
  fragment BillwerkMatchAccountFields on BillwerkMatchAccount {
    salesforceId
    name
    recordType
    b2c
    debitorAccount
    oldCustomerNumber
    billed
  }
`;

export const BILLWERK_PLAN_VARIANTS = gql`
  ${BILLWERK_PLAN_VARIANT_FIELDS}
  query BillwerkPlanVariants(
    $view: BillwerkPlanVariantView!
    $state: BillwerkPlanVariantState
    $search: String
    $offset: Int
  ) {
    billwerkPlanVariants(view: $view, state: $state, search: $search, offset: $offset) {
      billwerk {
        status
        loadedAt
        variantCount
      }
      salesforceSync {
        lastSucceededAt
        unreadableFields
        entriesCopied
      }
      withheld
      entryCounts {
        state
        count
      }
      itemCounts {
        state
        count
      }
      totalCount
      entries {
        salesforceId
        salesforceUrl
        priceBookId
        priceBookName
        priceBookActive
        priceBookStandard
        productId
        productName
        productFamily
        active
        currency
        billingItems
        planVariant {
          ...BillwerkPlanVariantFields
        }
      }
      items {
        salesforceId
        salesforceUrl
        name
        status
        accountName
        contractNumber
        contractUrl
        productName
        billed
        openNotification
        planVariant {
          ...BillwerkPlanVariantFields
        }
      }
    }
  }
`;

export const BILLWERK_CUSTOMER_MATCH = gql`
  ${BILLWERK_MATCH_ACCOUNT_FIELDS}
  query BillwerkCustomerMatch(
    $group: BillwerkCustomerMatchGroup
    $reason: BillwerkCustomerMatchReason
    $search: String
    $offset: Int
    $refreshAfter: Datetime
  ) {
    billwerkCustomerMatch(
      group: $group
      reason: $reason
      search: $search
      offset: $offset
      refreshAfter: $refreshAfter
    ) {
      billwerk {
        status
        loadedAt
        failedAt
        contracts
        customerCount
        checkedAt
      }
      salesforceSync {
        lastSucceededAt
        unreadableFields
        accountFieldsCopied
      }
      withheld
      counts {
        group
        reason
        count
      }
      totalCount
      rows {
        group
        reason
        debitorAccount
        accounts {
          ...BillwerkMatchAccountFields
        }
        payer {
          ...BillwerkMatchAccountFields
        }
        customers {
          id
          url
          name
          debitorAccount
          oldCustomerNumber
          externalCustomerId
          activeContract
        }
        mappedCustomerIds
        billedFacilities
      }
    }
  }
`;

export const CUSTOMER_SERVICE_CRM_OVERVIEW = gql`
  ${CRM_ROW_FIELDS}
  query CustomerServiceCrmOverview {
    customerServiceCrmOverview {
      syncedAt
      openCases
      waitingCases
      escalatedCases
      outsideSlaCases
      createdToday
      closedToday
      openByType {
        label
        count
      }
      openByStatus {
        label
        count
      }
      openByOwner {
        label
        count
      }
      oldestOpen {
        ...CrmRowFields
      }
      callsPerDay {
        day
        inbound
        outbound
      }
    }
  }
`;

export const MARKETING_OVERVIEW = gql`
  query MarketingOverview {
    marketingOverview {
      syncedAt
      months
      newLeads {
        label
        total
        months
        details {
          label
          total
          months
        }
      }
      conversions {
        label
        total
        months
      }
      medianDaysToConvert
      medianDaysToConvertByMonth
      openByStatus {
        label
        count
      }
    }
  }
`;

export const MARKETING_WEB_ORDERS = gql`
  query MarketingWebOrders {
    marketingWebOrders {
      syncedAt
      stuckAfterMinutes
      planVariantIdsKnown
      weeks {
        week
        orders
        stuck
      }
      orders {
        salesforceId
        title
        company
        status
        createdAt
        products {
          name
          quantity
          unitPrice
        }
        planVariantCount
        steps {
          kind
          done
          at
          on
          object
          salesforceId
          title
          salesforceUrl
        }
        stuck
        markers
        salesforceUrl
      }
    }
  }
`;

const FINANCE_ACCOUNT_REF_FIELDS = gql`
  fragment FinanceAccountRefFields on FinanceAccountRef {
    salesforceId
    name
    url
  }
`;

const FINANCE_CUSTOMER_REF_FIELDS = gql`
  fragment FinanceCustomerRefFields on FinanceCustomerRef {
    id
    name
    debitorAccount
    url
  }
`;

export const FINANCE_OVERVIEW = gql`
  ${FINANCE_ACCOUNT_REF_FIELDS}
  ${FINANCE_CUSTOMER_REF_FIELDS}
  query FinanceOverview(
    $list: FinanceOverviewList!
    $reconciliationStatus: FinanceReconciliationStatus
    $salesforceCheck: FinanceSalesforceCheck
    $billwerkCheck: FinanceBillwerkCheck
    $search: String
    $offset: Int
    $sort: FinanceOverviewSort
    $recentOnly: Boolean
  ) {
    financeOverview(
      list: $list
      reconciliationStatus: $reconciliationStatus
      salesforceCheck: $salesforceCheck
      billwerkCheck: $billwerkCheck
      search: $search
      offset: $offset
      sort: $sort
      recentOnly: $recentOnly
    ) {
      billwerk {
        status
        loadedAt
        contracts
        customerCount
        contractCount
      }
      salesforceSync {
        lastSucceededAt
        unreadableFields
      }
      withheld
      kpis {
        billedMonthlyValue
        billedItems
        billwerkActiveContracts
        billwerkActiveCustomers
        dunningContracts
        dunningRemaining
      }
      toDos {
        rule
        count
        monthlyValue
        legacy
        upcoming
      }
      reconciliationCounts {
        status
        count
        monthlyValue
      }
      salesforceCounts {
        check
        count
        monthlyValue
      }
      billwerkCounts {
        check
        count
        amount
      }
      planVariantGaps {
        count
        monthlyValue
      }
      recentDays
      totalCount
      reconciliation {
        status
        matchGroup
        matchReason
        debitorAccount
        accounts {
          ...FinanceAccountRefFields
        }
        customer {
          ...FinanceCustomerRefFields
        }
        variants {
          planVariantId
          name
          known
          url
          salesforceItems
          salesforceMonthlyValue
          billwerkQuantity
          billwerkContracts
        }
        salesforceItems
        monthlyValue
        billwerkContracts
        salesforceCreatedAt
        billwerkStartedAt
      }
      salesforceItems {
        salesforceId
        salesforceUrl
        name
        status
        accountName
        contractNumber
        contractUrl
        productName
        price
        startDate
        endDate
        billingTool
        createdAt
      }
      billwerkContracts {
        id
        customer {
          ...FinanceCustomerRefFields
        }
        accounts {
          ...FinanceAccountRefFields
        }
        lifecycleStatus
        planVariantId
        planVariantName
        quantity
        startDate
        endDate
        billingSuspended
        dunningLevel
        dunningRemaining
        dunningSince
      }
    }
  }
`;

export const CRM_DUPLICATE_RECORD_FIELDS = gql`
  fragment CrmDuplicateRecordFields on CrmDuplicateRecord {
    object
    salesforceId
    kind
    name
    createdAt
    createdByName
    company
    email
    phone
    addresses {
      street
      city
      state
      postalCode
      country
    }
    normalized {
      name
      company
      email
      phone {
        base
        extension
      }
      addresses {
        street
        house
        unit
        city
        state
        postalCode
        country
      }
    }
  }
`;

export const CRM_DUPLICATE_CANDIDATE_FIELDS = gql`
  fragment CrmDuplicateCandidateFields on CrmDuplicateCandidate {
    id
    fingerprint
    score
    reasons {
      code
      similarity
    }
    status
    reviewedAt
    left {
      ...CrmDuplicateRecordFields
    }
    right {
      ...CrmDuplicateRecordFields
    }
  }
  ${CRM_DUPLICATE_RECORD_FIELDS}
`;

export const CUSTOMER_SERVICE_CRM_DUPLICATES = gql`
  query CustomerServiceCrmDuplicates($input: CrmDuplicateListInput) {
    customerServiceCrmDuplicates(input: $input) {
      totalCount
      scannedAt
      scanState
      candidates {
        ...CrmDuplicateCandidateFields
      }
    }
  }
  ${CRM_DUPLICATE_CANDIDATE_FIELDS}
`;

export const LOGISTICS_INVENTORY = gql`
  ${LOGISTICS_HEXNODE_DEVICE}
  ${LOGISTICS_WORKSPACE_ONE_MATCH}
  query LogisticsInventory($input: LogisticsInventoryInput!) {
    logisticsInventory(input: $input) {
      deviceFilterSources {
        provider
        state
        syncedAt
        stale
      }
      counts {
        type
        status
        count
      }
      stock {
        type
        free
        readyToShip
        needsStaging
        inStockAtClient
      }
      totalCount
      rows {
        id
        name
        type
        status
        imei
        serialNumber
        hardwareSerialNumber
        simIccid
        appVersion
        qualityCheckDoneAt
        lastInStockAt
        simProvider {
          listed
          status
          connectivityStatus
          lastOnlineAt
        }
        hexnode {
          ...LogisticsHexnodeDevice
        }
        workspaceOne {
          ...LogisticsWorkspaceOneMatch
        }
        deletedInSalesforceAt
      }
      syncedAt
    }
  }
`;

export const LOGISTICS_INVENTORY_ITEM = gql`
  ${LOGISTICS_HEXNODE_DEVICE}
  ${LOGISTICS_WORKSPACE_ONE_MATCH}
  query LogisticsInventoryItem($id: UUID!) {
    logisticsInventoryItem(id: $id) {
      id
      salesforceUrl
      name
      type
      status
      inactiveReason
      inactiveDate
      imei
      serialNumber
      hardwareSerialNumber
      simIccid
      newDevice
      lastInStockAt
      lastInTransferAt
      appVersion
      appFlavour
      contentVersion
      createdAt
      modifiedAt
      simProvider {
        listed
        status
        connectivityStatus
        lastOnlineAt
        scannedAt
        checkedAt
      }
      hexnode {
        ...LogisticsHexnodeDevice
      }
      workspaceOne {
        ...LogisticsWorkspaceOneMatch
      }
      deletedInSalesforceAt
      qualityCheck {
        doneAt
        doneBy
        sticker
        mdmRemoved
        knoxRemoved
        noOpenAndroidUpdates
        unknownSourcesAllowed
        accessPointConfigured
        roamingActive
        screenTimeout30Min
        appAsStartApp
        cameraInAppAllowed
      }
      location {
        accountSalesforceId
        accountName
        contractSalesforceId
        contractNumber
        contractItemSalesforceId
        contractItemName
        contractItemStatus
        atClientDate
        asset {
          salesforceUrl
          name
          assetType
          slot
          latestOrder {
            salesforceId
            orderNumber
            name
            recordType
            status
            shippingDate
            completedDate
          }
        }
      }
      history {
        salesforceId
        accountSalesforceId
        accountName
        contractSalesforceId
        contractNumber
        contractItemSalesforceId
        contractItemName
        shippingDate
        atClientDate
        removedFromClientDate
        inStockDate
        endUserChange
        deletedInSalesforceAt
      }
    }
  }
`;

// Read live from WhereverSIM, so it is a query of its own: the unit page does not wait for it.
export const LOGISTICS_WHEREVER_SIM_DETAILS = gql`
  query LogisticsWhereverSimDetails($id: UUID!) {
    logisticsWhereverSimDetails(id: $id) {
      state
      sim {
        data
        errorCode
      }
      endpoint {
        data
        errorCode
      }
      connectivity {
        data
        errorCode
      }
    }
  }
`;

export const CONTENT_ANALYTICS_REPORT = gql`
  query ContentAnalyticsReport(
    $dateFrom: Date!
    $dateTo: Date!
    $mode: ContentAnalyticsMode!
    $contentKind: String
    $contentSubtype: String
    $first: Int!
    $offset: Int!
  ) {
    contentAnalyticsReport(
      dateFrom: $dateFrom
      dateTo: $dateTo
      mode: $mode
      contentKind: $contentKind
      contentSubtype: $contentSubtype
      first: $first
      offset: $offset
    ) {
      periodStart
      periodEnd
      latestDataDate
      contentTypes
      contentSubtypes
      totalCount
      usedCount
      unusedCount
      overview {
        contentKind
        sessionCount
        completedSessionCount
        unfinishedSessionCount
      }
      items {
        contentId
        title
        contentKind
        contentSubtype
        thumbnailFilename
        sessionCount
        completedSessionCount
        unfinishedSessionCount
      }
    }
  }
`;

const ACCOUNT_BILLING_INVOICES_FIELDS = gql`
  fragment AccountBillingInvoicesFields on AccountBillingInvoicePage {
    items {
      id
      number
      documentDate
      dueDate
      totalGross
      currency
    }
    nextCursor
  }
`;

const ACCOUNT_BILLING_PROFILE_FIELDS = gql`
  fragment AccountBillingProfileFields on AccountBillingProfile {
    id
    displayName
    externalCustomerId
    debtorAccount
    email
    phone
    vatId
    buyerReference
    deliveryMethod
    address {
      addressLine1
      addressLine2
      street
      houseNumber
      postalCode
      city
      state
      country
    }
  }
`;

const ACCOUNT_BILLING_SUBSCRIPTIONS_FIELDS = gql`
  fragment AccountBillingSubscriptionsFields on AccountBillingSubscriptionPage {
    items {
      id
      lifecycleStatus
      planVariantId
      planName
      billingPeriod {
        unit
        quantity
      }
      startDate
      endDate
      lastBillingDate
      nextBillingDate
      billedUntil
      currency
      balance
      paymentMethod
      billingSuspended
      recurringPaymentsPaused
      dunning {
        level
        remaining
        since
      }
    }
    nextCursor
  }
`;

const ACCOUNT_BILLING_LEDGER_FIELDS = gql`
  fragment AccountBillingLedgerFields on AccountBillingLedgerPage {
    items {
      id
      contractId
      timestamp
      type
      description
      amount
      currency
      paymentTransactionStatus
      paymentStatus
      invoiceId
    }
    nextCursor
  }
`;

export const ACCOUNT_BILLING = gql`
  query AccountBilling($salesforceId: String!) {
    customerServiceAccountBilling(salesforceId: $salesforceId) {
      availability
      matchingReason
      account {
        salesforceId
        name
      }
      billedThrough {
        salesforceId
        name
      }
      customer {
        id
        displayName
        externalCustomerId
        debtorAccount
        url
      }
      invoices {
        ...AccountBillingInvoicesFields
      }
      profile {
        ...AccountBillingProfileFields
      }
      subscriptions {
        ...AccountBillingSubscriptionsFields
      }
      ledger {
        ...AccountBillingLedgerFields
      }
    }
  }
  ${ACCOUNT_BILLING_INVOICES_FIELDS}
  ${ACCOUNT_BILLING_PROFILE_FIELDS}
  ${ACCOUNT_BILLING_SUBSCRIPTIONS_FIELDS}
  ${ACCOUNT_BILLING_LEDGER_FIELDS}
`;

export const ACCOUNT_BILLING_INVOICES = gql`
  query AccountBillingInvoices($salesforceId: String!, $after: String, $dateFrom: String, $dateTo: String) {
    customerServiceAccountBilling(salesforceId: $salesforceId) {
      availability
      matchingReason
      billedThrough {
        salesforceId
      }
      customer {
        id
      }
      invoices(after: $after, dateFrom: $dateFrom, dateTo: $dateTo) {
        ...AccountBillingInvoicesFields
      }
    }
  }
  ${ACCOUNT_BILLING_INVOICES_FIELDS}
`;

export const ACCOUNT_BILLING_PROFILE = gql`
  query AccountBillingProfile($salesforceId: String!) {
    customerServiceAccountBilling(salesforceId: $salesforceId) {
      availability
      matchingReason
      billedThrough {
        salesforceId
      }
      customer {
        id
      }
      profile {
        ...AccountBillingProfileFields
      }
    }
  }
  ${ACCOUNT_BILLING_PROFILE_FIELDS}
`;

export const ACCOUNT_BILLING_SUBSCRIPTIONS = gql`
  query AccountBillingSubscriptions($salesforceId: String!, $after: String) {
    customerServiceAccountBilling(salesforceId: $salesforceId) {
      availability
      matchingReason
      billedThrough {
        salesforceId
      }
      customer {
        id
      }
      subscriptions(after: $after) {
        ...AccountBillingSubscriptionsFields
      }
    }
  }
  ${ACCOUNT_BILLING_SUBSCRIPTIONS_FIELDS}
`;

export const ACCOUNT_BILLING_LEDGER = gql`
  query AccountBillingLedger($salesforceId: String!, $after: String) {
    customerServiceAccountBilling(salesforceId: $salesforceId) {
      availability
      matchingReason
      billedThrough {
        salesforceId
      }
      customer {
        id
      }
      ledger(after: $after) {
        ...AccountBillingLedgerFields
      }
    }
  }
  ${ACCOUNT_BILLING_LEDGER_FIELDS}
`;

export const LOGISTICS_WORKSPACE_ONE_DEVICES = gql`
  ${LOGISTICS_WORKSPACE_ONE_DEVICE}
  query LogisticsWorkspaceOneDevices {
    logisticsWorkspaceOneDevices {
      state
      syncedAt
      stale
      devices {
        device {
          ...LogisticsWorkspaceOneDevice
        }
        matchState
        inventory {
          id
          name
          hardwareSerialNumber
          deletedInSalesforceAt
        }
        customer {
          accountSalesforceId
          accountName
        }
        contradictions
      }
    }
  }
`;

export const LOGISTICS_DHL_SHIPPING_SETTINGS = gql`
  query LogisticsDhlShippingSettings {
    logisticsDhlShippingSettings {
      ...LogisticsDhlShippingSettingsFields
    }
  }
  ${LOGISTICS_DHL_SHIPPING_SETTINGS_FIELDS}
`;

export const LOGISTICS_DHL_ACCOUNT_SETTINGS = gql`
  query LogisticsDhlAccountSettings {
    logisticsDhlAccountSettings {
      ...LogisticsDhlAccountSettingsFields
    }
  }
  ${LOGISTICS_DHL_ACCOUNT_SETTINGS_FIELDS}
`;

export const LOGISTICS_ORDER_COMMAND_STATUS = gql`
  query LogisticsOrderCommandStatus($salesforceId: String!) {
    logisticsOrderCommandStatus(salesforceId: $salesforceId) {
      orderId
      commandId
      state
      refusal
      messages
    }
  }
`;

export const LOGISTICS_SHIPPING_LABEL_STATE = gql`
  query LogisticsShippingLabelState($salesforceId: String!) {
    logisticsShippingLabelState(salesforceId: $salesforceId) {
      orderId
      enabled
      pilot
      refusal
      label {
        ...LogisticsShippingLabelFields
      }
      attempt {
        requestId
        command
        labelId
        returnLabel
        outcome
        refusal
        messages
        salesforceCommandId
      }
    }
  }
  ${LOGISTICS_SHIPPING_LABEL_FIELDS}
`;
